import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { ICollaborateur } from 'src/app/shared/models/collaborateur';
import { IDevisCollaborateur } from 'src/app/shared/models/devis-collaborateur';
import { CollaborateurService } from '../../collaborateurs-list/collaborateur.service';
import { DevisCollaborateurService } from './devis-collaborateur.service';

@Component({
  selector: 'app-devis-collaborateurs',
  templateUrl: './devis-collaborateurs.component.html',
  styleUrls: ['./devis-collaborateurs.component.less']
})
export class DevisCollaborateursComponent implements OnInit, OnChanges {

  /** Identifiant du devis courant */
  @Input() devisId!: string;

  /** Passe le composant en lecture seule (ex: devis déjà accepté/refusé/archivé) */
  @Input() readonly = false;

  loading = false;
  saving = false;

  /** Collaborateurs actuellement associés au devis (vue "historique métier" CDC règle 7) */
  associatedCollaborateurs: IDevisCollaborateur[] = [];

  /** Collaborateurs actifs sélectionnables (CDC règle 8) */
  activeCollaborateurs: ICollaborateur[] = [];

  selectionControl = new UntypedFormControl([]);

  openingCvId: string | null = null;

  constructor(
    private devisCollaborateurService: DevisCollaborateurService,
    private collaborateurService: CollaborateurService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['devisId'] && !changes['devisId'].firstChange) {
      this.loadAll();
    }
  }

  private loadAll(): void {
    if (!this.devisId) return;

    this.loading = true;

    this.collaborateurService.search({ active: true, page: 0, size: 500, sort: 'nom,asc' }).subscribe({
      next: (page) => {
        this.activeCollaborateurs = page.content;
        this.loadAssociation();
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement des collaborateurs actifs'),
          'error-snack-bar'
        );
        this.loading = false;
      }
    });
  }

  private loadAssociation(): void {
    this.devisCollaborateurService.getCollaborateurs(this.devisId).subscribe({
      next: (data) => {
        this.associatedCollaborateurs = data;
        this.selectionControl.setValue(data.map(c => c.collaborateurId));
        this.loading = false;
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement des collaborateurs associés'),
          'error-snack-bar'
        );
        this.loading = false;
      }
    });
  }

  /**
   * Collaborateurs associés mais devenus inactifs entre-temps : le CDC (règle 7) impose
   * de continuer à les afficher dans l'historique du devis même s'ils n'apparaissent
   * plus dans la liste de sélection.
   */
  get inactiveAssociatedCollaborateurs(): IDevisCollaborateur[] {
    const activeIds = new Set(this.activeCollaborateurs.map(c => c.collabId));
    return this.associatedCollaborateurs.filter(c => !activeIds.has(c.collaborateurId));
  }

  postesLabel(collaborateur: ICollaborateur): string {
    if (!collaborateur.postes || collaborateur.postes.length === 0) return '';
    return collaborateur.postes.map(p => p.libelle).join(', ');
  }

  /** Collaborateurs actifs actuellement cochés dans la sélection, pour le tableau récapitulatif */
  get selectedCollaborateurs(): ICollaborateur[] {
    const selectedIds: string[] = this.selectionControl.value ?? [];
    return this.activeCollaborateurs.filter(c => selectedIds.includes(c.collabId));
  }

  /** Ouvre le CV dans un nouvel onglet du navigateur (rendu PDF natif), pas dans le visualisateur en dialog */
  openCv(collaborateur: ICollaborateur): void {
    this.openingCvId = collaborateur.collabId;

    this.collaborateurService.getCvBlob(collaborateur.collabId).subscribe({
      next: (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank');
        this.openingCvId = null;
      },
      error: (err) => {
        this.openingCvId = null;
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'ouverture du CV'),
          'error-snack-bar'
        );
      }
    });
  }

  save(): void {
    this.saving = true;

    this.devisCollaborateurService.updateCollaborateurs(this.devisId, {
      collaborateursIds: this.selectionControl.value ?? []
    }).subscribe({
      next: () => {
        this.saving = false;
        this.snackbarService.openSnackBar('Association des collaborateurs enregistrée');
        this.loadAssociation();
      },
      error: (err) => {
        this.saving = false;
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'enregistrement de l\'association'),
          'error-snack-bar'
        );
      }
    });
  }

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}